import { useQuery } from "@apollo/client";
import { Typography, Button, Box } from "@mui/material";
import { DateTime } from "luxon";
import { ReactElement, useState } from "react";

import { STATES } from "@stw/states";

import {
  DonationHistorySource,
  GetSubaccountDonationMetadataQuery,
  GetSubaccountDonationMetadataQueryVariables,
  SearchDonationHistoryFormNamesQuery,
  SearchDonationHistoryFormNamesQueryVariables,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { ControlsRow } from "~frontend/components/ControlsPaper";
import { FlexBox } from "~frontend/components/FlexBox";
import { TableControls } from "~frontend/components/Table";
import {
  GET_SUBACCOUNT_DONATION_METADATA,
  SEARCH_DONATION_HISTORY_FORM_NAMES,
} from "~frontend/graphql/donationHistory";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { formatDateTime } from "~frontend/util/dateFormat";
import useDebounce from "~frontend/util/useDebounce";

import { ImportDonationHistoryDialog } from "./ImportDonationHistoryDialog";

export function DonationHistoryTableControls({
  donationDateRange,
  onChangeDonationDateRange,
  donorStates,
  onChangeDonorStates,
  donationSources,
  onChangeDonationSources,
  donationForms,
  onChangeDonationForms,
  search,
  onChangeSearch,
  importDonationHistoryDisabled,
}: {
  donationDateRange: { startDate: DateTime; endDate: DateTime } | null;
  onChangeDonationDateRange: (
    newRange: {
      startDate: DateTime;
      endDate: DateTime;
    } | null
  ) => void;
  donorStates: string[];
  onChangeDonorStates: (newDonorStates: string[]) => void;
  donationSources: string[]; // TODO: better type
  onChangeDonationSources: (newDonationSources: string[]) => void;
  donationForms: string[];
  onChangeDonationForms: (newDonationForms: string[]) => void;
  search: string;
  onChangeSearch: (newSearch: string) => void;
  importDonationHistoryDisabled?: boolean;
}): ReactElement {
  const { subaccount } = useSubaccount()!;

  const [donationFormSearch, setDonationFormSearch] = useState("");
  const debouncedDonationFormSearch = useDebounce(donationFormSearch, 300);
  const donationFormsQuery = useQuery<
    SearchDonationHistoryFormNamesQuery,
    SearchDonationHistoryFormNamesQueryVariables
  >(SEARCH_DONATION_HISTORY_FORM_NAMES, {
    variables: {
      input: {
        subaccountId: subaccount.id,
        search: debouncedDonationFormSearch,
      },
    },
    skip: debouncedDonationFormSearch.length < 3,
  });

  const donationHistoryInfoQuery = useQuery<
    GetSubaccountDonationMetadataQuery,
    GetSubaccountDonationMetadataQueryVariables
  >(GET_SUBACCOUNT_DONATION_METADATA, {
    variables: { input: { subaccountId: subaccount.id } },
  });

  const [importDonationHistoryDialogOpen, setImportDonationHistoryDialogOpen] =
    useState(false);

  return (
    <>
      <TableControls
        filters={[
          {
            id: "donation-date",
            label: "Donation Date (Pacific Time)",
            type: "dateRange",
            maxDate: DateTime.now().setZone("America/Los_Angeles"),
            value: donationDateRange,
            onChange: onChangeDonationDateRange,
          },
          {
            id: "donor-state",
            label: "Donor State",
            type: "dropdown",
            multi: true,
            options: STATES.map(state => ({
              value: state.abbreviation,
              label: state.name,
            })),
            value: donorStates.filter(state => !!state) as string[],
            onChange: onChangeDonorStates,
          },
          {
            id: "source",
            label: "Source",
            type: "dropdown",
            multi: true,
            options: [
              {
                value: DonationHistorySource.ActBlueWebhook,
                label: "ActBlue Webhook",
              },
              {
                value: DonationHistorySource.ActBlueBackfill,
                label: "ActBlue Backfill",
              },
            ],
            value: donationSources.filter(source => !!source) as string[],
            onChange: onChangeDonationSources,
          },
          {
            id: "form",
            label: "Form",
            type: "autocomplete",
            multi: true,
            search: donationFormSearch,
            onChangeSearch: setDonationFormSearch,
            optionsLoading: !donationFormsQuery.data,
            optionsLoadingText:
              donationFormSearch.length < 3
                ? "Please enter at least 3 characters..."
                : "Loading...",
            noOptionsText: "No matches found",
            options:
              donationFormsQuery.data?.searchDonationHistoryFormNames
                .formNames ?? [],
            value: donationForms.filter(form => !!form) as string[],
            onChange: (newValue: string[]) => {
              onChangeDonationForms(newValue);
              setDonationFormSearch("");
            },
          },
        ]}
        search={{
          value: search,
          onChange: onChangeSearch,
          placeholder: "Search by donor name, phone number, or campaign",
        }}
        controlsFooter={
          <ControlsRow>
            <FlexBox
              justifyContent="space-between"
              alignItems="center"
              sx={{ width: "100%" }}
            >
              <AsyncWrapper
                data={donationHistoryInfoQuery.data}
                loading={donationHistoryInfoQuery.loading}
                errors={donationHistoryInfoQuery.error}
              >
                {data =>
                  data.getSubaccountDonationMetadata ? (
                    <FlexBox gap="16px">
                      <Typography variant="body2">
                        Earliest Donation:{" "}
                        {formatDateTime(
                          data.getSubaccountDonationMetadata
                            .earliestDonationDate
                        )}
                      </Typography>
                      <Typography variant="body2">
                        Most Recent Donation:{" "}
                        {formatDateTime(
                          data.getSubaccountDonationMetadata.latestDonationDate
                        )}
                      </Typography>
                    </FlexBox>
                  ) : (
                    <Box />
                  )
                }
              </AsyncWrapper>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setImportDonationHistoryDialogOpen(true)}
                disabled={importDonationHistoryDisabled}
              >
                Import donation history
              </Button>
            </FlexBox>
          </ControlsRow>
        }
      />
      <ImportDonationHistoryDialog
        open={importDonationHistoryDialogOpen}
        onClose={() => setImportDonationHistoryDialogOpen(false)}
      />
    </>
  );
}
